import { WSList, WSText } from "@wingspanhq/fe-component-library";
import {
  ComplianceEntityType,
  ComplianceEntityUsTaxStatus,
  VerificationLevel
} from "@wingspanhq/wingspan-api-sdk";
import { useEffect, useMemo, useRef } from "react";
import { queriesComplianceEntities, queriesOnboarding } from "@query";
import { FormAccountType } from "../../components/FormAccountType";
import { useOnboardignModules, useOnboardingContext } from "../../Context";
import { OnboardingModuleCreateAccount } from "../../types";
import { CreateAccountContext } from "./types";
import { FlowStep } from "../../../../shared/components/FlowSwitch/types";

export const AccountType: React.FC<FlowStep> = ({ onNext }) => {
  const [context, setContext] = useOnboardingContext<CreateAccountContext>();

  const pendingNextRef = useRef(false);

  useEffect(() => {
    if (pendingNextRef.current && context.accountType) {
      pendingNextRef.current = false;
      onNext?.();
    }
  }, [context.accountType, onNext]);

  const modules = useOnboardignModules();
  const onboardingModule = modules.find(m => m.type === "create_account") as
    | OnboardingModuleCreateAccount
    | undefined;
  const accountTypes = onboardingModule?.options?.accountTypes;
  const verificationLevel = onboardingModule?.options?.verificationLevel;
  const forcedCountry = onboardingModule?.options?.country;
  const allowUSInternationalPerson =
    onboardingModule?.options?.allowUSInternationalPerson;

  const verificationLevelIsBanking = verificationLevel === "Banking";

  const queryComplianceEntities =
    queriesComplianceEntities.listComplianceEntities.useQuery({});
  const queryOnboardingStatus = queriesOnboarding.getOnboardingStatus.useQuery(
    {}
  );
  const verificationStatus =
    verificationLevel === "Banking"
      ? queryOnboardingStatus.data?.verifications?.banking
      : queryOnboardingStatus.data?.verifications?.tax;

  const defaultData = useMemo(() => {
    const primary = queryComplianceEntities.data?.data?.find(
      entity => !entity.subject
    );

    const country =
      forcedCountry ||
      primary?.jurisdictionCountry ||
      primary?.physicalAddress?.country;

    const isUsPerson =
      primary?.usTaxStatus === ComplianceEntityUsTaxStatus.US_PERSON ||
      (primary?.taxIdentifiers ?? []).some(
        identifier => identifier.countryCode === "US"
      );

    return {
      country,
      type: primary
        ? primary.type === ComplianceEntityType.BUSINESS
          ? ("Business" as const)
          : ("Individual" as const)
        : undefined,
      isUsPerson: !!country && country !== "US" && isUsPerson
    };
  }, [queryComplianceEntities.data, forcedCountry]);

  return (
    <WSList gap="2XL">
      <FormAccountType
        accountTypes={accountTypes}
        allowUSInternationalPerson={allowUSInternationalPerson}
        verificationLevelIsBanking={verificationLevelIsBanking}
        disabled={verificationStatus === VerificationLevel.VERIFIED}
        countryDisabled={!!forcedCountry}
        defaultValues={context.accountType || defaultData}
        onSubmit={data => {
          pendingNextRef.current = true;
          setContext(prev => ({
            ...prev,
            accountType: data
          }));
        }}
        onChange={data => {
          setContext(prev => ({
            ...prev,
            accountType: data
          }));
        }}
      />

      <WSText.ParagraphXs color="gray400">
        This information is required to set up your account. If you exit now,
        you'll return to this page when you sign in again.
      </WSText.ParagraphXs>
    </WSList>
  );
};
